import { lazyInitializeCalendarsDataForEventDeeplink } from 'owa-calendar-bootstrap-utils';
import type { CalendarComposePopoutConfig, FullComposeViewState } from 'owa-calendar-compose-types';
import type { FullComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import { FULL_COMPOSE_VIEW_TYPE_POPOUT } from 'owa-calendar-helpers-types/lib/ComposeTypes';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { ClientItemId, getUserMailboxInfo, MailboxInfo } from 'owa-client-ids';
import { owaDate, timestamp, utcDate } from 'owa-datetime';
import { convertRestIdToEwsId } from 'owa-identifiers';
import { getDataFromParentWindow } from 'owa-popout-v2';
import EventScope from 'owa-service/lib/contract/EventScope';
import { getUserConfiguration } from 'owa-session-store';
import {
    lazyShowFullComposeInEditMode,
    lazyShowFullComposeForLegacyPopout,
    lazyShowFullComposeFromDeeplink,
    lazyShowFullComposeToEdit,
} from 'owa-calendar-compose-form-store';
import { lazyGenerateInitialDuplicateEvent } from 'owa-calendar-duplicate-event';
import {
    getOptionsForFeature,
    OwsOptionsFeatureType,
    BohemiaOptions,
} from 'owa-outlook-service-options';
import registerOnShowFullComposeOrchestrator from '../utils/registerOnShowFullComposeOrchestrator';
import { initBootCalendarCloudSettings } from 'owa-calendar-cloud-settings/lib/bootIndex';
import { initializeDynamicLayout } from 'owa-layout';
import { getId } from '@fluentui/react/lib/Utilities';
import { isFeatureEnabled } from 'owa-feature-flags';

export interface CalendarComposeRouteParameters {
    itemId?: string;
    account?: string;
}

export async function deepLinkComposeRouteHandler(parameters: CalendarComposeRouteParameters) {
    registerOnShowFullComposeOrchestrator();

    initBootCalendarCloudSettings();

    // Initialize calendars
    await lazyInitializeCalendarsDataForEventDeeplink.importAndExecute();

    // Initialize dynamic layout
    // displayAds not enabled since this is a calendar scenario
    initializeDynamicLayout(false /* areDisplayAdsEnabled */);

    let mailboxInfo: MailboxInfo = null;
    if (parameters.account) {
        mailboxInfo = getUserMailboxInfo(parameters.account);
    }

    const popoutConfig: CalendarComposePopoutConfig | null = await getDataFromParentWindow(
        parseCalendarComposePopoutConfig
    );

    if (popoutConfig?.includeFluidOnPopout) {
        let options = getOptionsForFeature<BohemiaOptions>(OwsOptionsFeatureType.Bohemia);
        options.fluidEnabledForTenant = true;
    }

    // There are multiple combinations of URL parameters and config data
    // which can be used to open a deeplink compose form. Attempt to open
    // each in priority order
    if (tryInitializeWithItemIdParameter(parameters, mailboxInfo)) {
        return;
    } else if (tryInitializeWithComposeConfig(popoutConfig)) {
        return;
    } else if (tryInitializeWithDraftItemId(popoutConfig, mailboxInfo)) {
        return;
    } else if (tryInitializeWithDuplicateEvent(popoutConfig)) {
        return;
    } else if (tryInitializeFromPriorState(popoutConfig)) {
        return;
    } else if (tryInitializeWithPartialEvent(popoutConfig)) {
        return;
    } else {
        // Fallback to initializing full compose from any URL provided parameters
        lazyShowFullComposeFromDeeplink.importAndExecute();
    }
}

function parseCalendarComposePopoutConfig(json: string): CalendarComposePopoutConfig {
    try {
        const popoutConfig = JSON.parse(json) as CalendarComposePopoutConfig;
        if (popoutConfig) {
            fixComposeViewState(popoutConfig.viewState);
            fixEvent(popoutConfig.event);
            fixEvent(popoutConfig.originalEvent);
        }
        return popoutConfig;
    } catch {
        return null;
    }
}

function fixEvent(event: CalendarEvent | null) {
    if (event) {
        const defaultTz = getUserConfiguration()?.UserOptions?.TimeZone;
        // OwaDates get serialized into the UTC isoString. (see owaDate::toJSON)
        // So we interpret them as UTC dates and then convert to the appropriate TZ
        event.Start = owaDate(event.StartTimeZoneId || defaultTz, utcDate(event.Start));
        event.End = owaDate(event.EndTimeZoneId || defaultTz, utcDate(event.End));
    }
}

function fixComposeViewState(viewState: FullComposeViewState | null) {
    if (viewState) {
        // Clear freeBusyData since it will be initialized again
        viewState.freeBusyData = null;

        // Set view type to PopOut
        viewState.viewType = FULL_COMPOSE_VIEW_TYPE_POPOUT;
    }
}

function tryInitializeWithItemIdParameter(
    parameters: CalendarComposeRouteParameters,
    mailboxInfo: MailboxInfo | null
) {
    if (!parameters.itemId) {
        return false;
    }

    // In case item Id is rest id, convert to EwsId.
    // If itemId is EWSId, then convertRestIdToEwsId returns the same id without any changes
    const id = convertRestIdToEwsId(parameters.itemId);

    const itemId: ClientItemId = {
        Id: id,
        mailboxInfo: mailboxInfo,
    };

    openComposeWithItemId(itemId, getComposeEntrySource(null /* popoutConfig */));

    return true;
}

function tryInitializeWithComposeConfig(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig?.composeConfig) {
        return false;
    }

    const { composeConfig } = popoutConfig;
    composeConfig.viewType = FULL_COMPOSE_VIEW_TYPE_POPOUT;

    const formId = isFeatureEnabled('cal-multiple-compose-forms')
        ? getId('CalendarCompose_')
        : null;

    lazyShowFullComposeToEdit.importAndExecute(formId, composeConfig);

    return true;
}

function tryInitializeWithDraftItemId(
    popoutConfig: CalendarComposePopoutConfig | null,
    mailboxInfo: MailboxInfo | null
) {
    if (!popoutConfig?.draftItemId) {
        return false;
    }

    const {
        draftItemId,
        extractionSourceId,
        internetMessageId,
        shouldInitializeForNewDraft,
    } = popoutConfig;

    const itemId: ClientItemId = {
        Id: draftItemId,
        mailboxInfo: mailboxInfo,
    };

    openComposeWithItemId(
        itemId,
        getComposeEntrySource(popoutConfig),
        extractionSourceId,
        internetMessageId,
        shouldInitializeForNewDraft
    );

    return true;
}

function tryInitializeWithDuplicateEvent(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig?.event?.ItemId || !popoutConfig?.shouldDuplicate) {
        return false;
    }

    lazyGenerateInitialDuplicateEvent.importAndExecute(popoutConfig.event.ItemId).then(result => {
        lazyShowFullComposeToEdit.importAndExecute(null /* formId */, {
            fullComposeEntrySource: getComposeEntrySource(popoutConfig),
            eventUpdates: result.event,
            createCollabSpaceOnInitProps: result.createCollabSpaceOnInitProps,
            viewType: FULL_COMPOSE_VIEW_TYPE_POPOUT,
        });
    });

    return true;
}

function tryInitializeFromPriorState(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig?.viewState || !popoutConfig?.event || !popoutConfig?.originalEvent) {
        return false;
    }

    const { viewState, event, originalEvent, shouldOpenSchedulingAssistant } = popoutConfig;

    lazyShowFullComposeForLegacyPopout.importAndExecute(
        viewState,
        event,
        originalEvent,
        shouldOpenSchedulingAssistant
    );

    return true;
}

function tryInitializeWithPartialEvent(popoutConfig: CalendarComposePopoutConfig | null) {
    if (!popoutConfig) {
        return false;
    }

    const { event, eventScope, eventHasUpdates, createCollabSpaceOnInitProps } = popoutConfig;

    lazyShowFullComposeInEditMode.importAndExecute(
        event ?? null,
        eventScope ?? EventScope.Default,
        !!eventHasUpdates /* itemHasUpdates */,
        getComposeEntrySource(popoutConfig),
        FULL_COMPOSE_VIEW_TYPE_POPOUT,
        null /* extractionSourceId */,
        null /* internetMessageId */,
        false /* shouldInitializeForNewDraft */,
        false /* shouldRespectStartEndTime */,
        createCollabSpaceOnInitProps
    );

    return true;
}

function getComposeEntrySource(popoutConfig: CalendarComposePopoutConfig | null) {
    return popoutConfig?.composeEntrySource ?? ('Popout' as FullComposeEntrySourceType);
}

function openComposeWithItemId(
    itemId: ClientItemId,
    composeEntrySource: FullComposeEntrySourceType,
    extractionSourceId?: string,
    internetMessageId?: string,
    shouldInitializeForNewDraft?: boolean
) {
    const partialItem: Partial<CalendarEvent> = {
        ItemId: itemId,
    };

    const currentTimeInMs = timestamp();

    // post message back to parent window for removing
    // draft item on user calendar (minimize user confusion)
    window.addEventListener('beforeunload', event => {
        window.opener.postMessage(
            JSON.stringify({
                source: 'calDraftItem',
                id: itemId.Id,
                entry: composeEntrySource,
                EID: extractionSourceId,
                start: currentTimeInMs,
            }),
            window.location.origin
        );
    });

    lazyShowFullComposeInEditMode.importAndExecute(
        partialItem,
        EventScope.Default,
        false /* itemHasUpdates */,
        composeEntrySource,
        FULL_COMPOSE_VIEW_TYPE_POPOUT,
        extractionSourceId,
        internetMessageId,
        shouldInitializeForNewDraft
    );
}
